import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  timeout: 15000,
  withCredentials: false,
});

let csrfTokenCache = { token: null, fetchedAt: 0, ttlMs: 30 * 60 * 1000 };
let inflight = null;

async function fetchCsrfToken() {
  const res = await axios.get(`${API}/csrf`, { timeout: 10000 });
  const data = res?.data || {};
  const ttlMs = (Number(data.ttl) || 1800) * 1000;
  csrfTokenCache = {
    token: data.token || null,
    fetchedAt: Date.now(),
    ttlMs,
  };
  return csrfTokenCache.token;
}

export async function getCsrfToken() {
  const now = Date.now();
  if (
    csrfTokenCache.token &&
    now - csrfTokenCache.fetchedAt < csrfTokenCache.ttlMs - 60_000
  ) {
    return csrfTokenCache.token;
  }
  if (!inflight) {
    inflight = fetchCsrfToken().finally(() => {
      inflight = null;
    });
  }
  return inflight;
}

export function invalidateCsrfToken() {
  csrfTokenCache = { token: null, fetchedAt: 0, ttlMs: csrfTokenCache.ttlMs };
}

api.interceptors.request.use(async (config) => {
  const method = (config.method || "get").toLowerCase();
  if (["post", "put", "patch", "delete"].includes(method)) {
    try {
      const token = await getCsrfToken();
      if (token) {
        config.headers = config.headers || {};
        config.headers["X-CSRF-Token"] = token;
      }
    } catch (err) {
      // proceed without token — backend will reject with 403
      // and the next attempt will re-fetch
    }
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail;
    if (status === 403 && detail === "csrf_failed") {
      invalidateCsrfToken();
    }
    return Promise.reject(error);
  },
);

export default api;
